import React from "react";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import type { BaseEntity, FieldValue } from "../../types";
import type { EditingState } from "../types";

interface EditableFieldProps<TData extends BaseEntity> {
  item: TData;
  fieldId: string;
  editingCell: EditingState | null;
  editValue: string;
  onEditChange: (value: string) => void;
  onEdit: (rowId: string, fieldId: string, currentValue: FieldValue) => void;
  onSave: (rowId: string, fieldId: string) => void;
  onCancel: () => void;
  renderValue: (item: TData, fieldId: string) => React.ReactNode;
  onClick?: (e: React.MouseEvent) => void;
  className?: string;
  /** Display-only: no inline edit; clicks bubble to parent (e.g. card opens detail modal) */
  readOnly?: boolean;
}

export function EditableField<TData extends BaseEntity>({
  item,
  fieldId,
  editingCell,
  editValue,
  onEditChange,
  onEdit,
  onSave,
  onCancel,
  renderValue,
  onClick,
  className = "",
  readOnly = false,
}: EditableFieldProps<TData>) {
  const isEditing =
    !readOnly && editingCell?.rowId === item.id && editingCell?.fieldId === fieldId;

  if (readOnly) {
    return (
      <div
        className={cn(
          "min-w-0 text-left break-words",
          className
        )}
      >
        {renderValue(item, fieldId)}
      </div>
    );
  }

  if (isEditing) {
    return (
      <div onClick={onClick}>
        <Input
          value={editValue}
          onChange={(e) => onEditChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              onSave(item.id, fieldId);
            } else if (e.key === "Escape") {
              onCancel();
            }
          }}
          onBlur={() => onSave(item.id, fieldId)}
          autoFocus
          className="h-8"
        />
      </div>
    );
  }

  return (
    <div
      onClick={(e) => {
        onClick?.(e);
        onEdit(item.id, fieldId, item[fieldId as keyof TData]);
      }}
      className={cn("hover:bg-muted cursor-text rounded p-1", className)}
    >
      {renderValue(item, fieldId)}
    </div>
  );
}
